import * as React from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  IconButton,
  Tooltip,
  ListItemText,
  ListSubheader,
  Typography,
  CircularProgress,
  Box,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcCallIcon from "@mui/icons-material/AddIcCall";
import QuickLogPopover from "./ContactHistory/QuickLogPopover";
import { useNavigate } from "react-router-dom";
import useGlobalSearch from "../hooks/useGlobalSearch";
import { useTokens } from "../theme";

export default function GlobalSearch({ open, onClose }) {
  const tokens = useTokens();
  const [term, setTerm] = React.useState("");
  const { clients, groups, agents, isLoading, isError } = useGlobalSearch(term);
  const navigate = useNavigate();
  const [quickLog, setQuickLog] = React.useState({ row: null, anchorEl: null });

  const trimmedTerm = term.trim();
  const hasResults = clients.length + groups.length + agents.length > 0;

  const handleClose = () => {
    setTerm("");
    onClose();
  };

  const clientPath = (client) =>
    `/clientDashboard?id=${client.agentId}&firstName=${encodeURIComponent(client.firstName)}&lastName=${encodeURIComponent(client.lastName)}`;
  const groupPath = (group) =>
    `/groupDashboard?adminId=${group.agentId}&name=${encodeURIComponent(group.companyName)}`;
  const agentPath = (agent) =>
    `/adminTable?tab=agents&firstName=${encodeURIComponent(agent.firstName)}&lastName=${encodeURIComponent(agent.lastName)}`;

  // Every result in display order, so the arrow keys can walk across sections.
  const paths = [
    ...clients.map(clientPath),
    ...groups.map(groupPath),
    ...agents.map(agentPath),
  ];
  const [activeIndex, setActiveIndex] = React.useState(0);
  React.useEffect(() => setActiveIndex(0), [clients, groups, agents]);
  const activePath = paths[activeIndex] || null;
  const groupOffset = clients.length;
  const agentOffset = clients.length + groups.length;

  const goTo = (path) => {
    navigate(path);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { minHeight: "320px" } }}
    >
      <DialogContent sx={{ padding: "16px" }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search clients, groups, agents..."
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              if (!paths.length) return;
              event.preventDefault();
              const step = event.key === "ArrowDown" ? 1 : -1;
              setActiveIndex((prev) => (prev + step + paths.length) % paths.length);
            } else if (event.key === "Enter" && activePath && !isLoading) {
              event.preventDefault();
              goTo(activePath);
            }
          }}
          helperText={
            hasResults && !isLoading ? "↑ ↓ to move, Enter to open the highlighted result" : " "
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: tokens.mutedForeground }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ marginTop: "12px", maxHeight: "400px", overflowY: "auto" }}>
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading && isError && (
            <Typography sx={{ padding: 2, textAlign: "center", color: "error.main" }}>
              Something went wrong while searching. Please try again.
            </Typography>
          )}

          {!isLoading && !isError && trimmedTerm && !hasResults && (
            <Typography sx={{ padding: 2, textAlign: "center", color: "text.secondary" }}>
              No results found
            </Typography>
          )}

          {!isLoading && clients.length > 0 && (
            <List dense subheader={<ListSubheader>Clients</ListSubheader>}>
              {clients.map((client, index) => (
                <ListItem
                  key={`client-${client.policyId}`}
                  disablePadding
                  secondaryAction={
                    <Tooltip title="Log contact">
                      <IconButton
                        edge="end"
                        aria-label="Log contact"
                        onClick={(event) => setQuickLog({ row: client, anchorEl: event.currentTarget })}
                      >
                        <AddIcCallIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton selected={activeIndex === index} onClick={() => goTo(clientPath(client))}>
                    <ListItemText
                      primary={`${client.firstName} ${client.lastName}`}
                      secondary={client.policyStatus}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {!isLoading && groups.length > 0 && (
            <List dense subheader={<ListSubheader>Groups</ListSubheader>}>
              {groups.map((group, index) => (
                <ListItemButton
                  key={`group-${group.groupId}`}
                  selected={activeIndex === groupOffset + index}
                  onClick={() =>
                    goTo(groupPath(group))
                  }
                >
                  <ListItemText primary={group.companyName} secondary={group.policyStatus} />
                </ListItemButton>
              ))}
            </List>
          )}

          {!isLoading && agents.length > 0 && (
            <List dense subheader={<ListSubheader>Agents</ListSubheader>}>
              {agents.map((agent, index) => (
                <ListItemButton
                  key={`agent-${agent.id}`}
                  selected={activeIndex === agentOffset + index}
                  onClick={() =>
                    goTo(agentPath(agent))
                  }
                >
                  <ListItemText
                    primary={`${agent.firstName} ${agent.lastName}`}
                    secondary={agent.email}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <QuickLogPopover
        anchorEl={quickLog.anchorEl}
        row={quickLog.row}
        onClose={() => setQuickLog({ row: null, anchorEl: null })}
      />
    </Dialog>
  );
}
